import React, { useContext, useState, useRef, useEffect } from 'react';
import SignaturePad from 'react-signature-canvas';
import { CustomButton } from '../FormInput';
import { Grid, FormLabel } from '@material-ui/core';
import BorderColorIcon from '@material-ui/icons/BorderColor';
import DeleteIcon from '@material-ui/icons/Delete';
import { AppStorage } from '../Service/storage-service';
import AppContext from '../../App/AppContext';
import './signature.scss';

export const CustomeSignPad = ({ sign, name, content, onChange, clearSig, isRequired, allowEdit = false, style = {} }) => {
    const { showToast } = useContext(AppContext);
    const signCanvas = useRef(null);

    const [state, setState] = useState({
        isSignExist: false,
        signatureDataURL: null,
        isCanvasDisabled: false
    });

    // Load signature when `sign` or `allowEdit` changes
    useEffect(() => {
        if (!signCanvas.current) return;

        signCanvas.current.clear();
        signCanvas.current.on();

        const hasSign = !!sign;

        if (hasSign) {
            signCanvas.current.fromDataURL(sign);
            if (!allowEdit) {
                signCanvas.current.off(); // Disable only if not editable
            }
        }

        setState((prev) => ({
            ...prev,
            signatureDataURL: hasSign ? sign : null,
            isSignExist: hasSign && !allowEdit,
            isCanvasDisabled: hasSign && !allowEdit
        }));
    }, [sign, allowEdit]);

    // Redraw on screen resize/orientation change
    useEffect(() => {
        const handleResize = () => {
            if (!signCanvas.current || !state.signatureDataURL) return;

            signCanvas.current.clear();
            signCanvas.current.fromDataURL(state.signatureDataURL);

            if (state.isCanvasDisabled) {
                signCanvas.current.off();
            } else {
                signCanvas.current.on();
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [state.signatureDataURL, state.isCanvasDisabled]);

    // Use saved signature from AppStorage
    const useDefaultSig = () => {
        const userSign = AppStorage.getSignature();
        if (userSign) {
            signCanvas.current.fromDataURL(userSign);
            signCanvas.current.on(); // ✅ Keep editable

            setState((st) => ({
                ...st,
                isSignExist: true,
                signatureDataURL: userSign,
                isCanvasDisabled: false
            }));

            onChange?.({ [name]: userSign });
        } else {
            showToast('User signature not found');
        }
    };

    // Clear pad
    const clearLeaderSign = () => {
        signCanvas.current.clear();
        signCanvas.current.on();

        setState((st) => ({
            ...st,
            isSignExist: false,
            signatureDataURL: null,
            isCanvasDisabled: false
        }));

        clearSig?.();
    };

    // When user draws a signature
    const handleSignatureEnd = () => {
        const trimmed = signCanvas.current.getTrimmedCanvas().toDataURL();

        setState((st) => ({
            ...st,
            signatureDataURL: trimmed,
            isSignExist: !allowEdit ? true : false
        }));

        onChange?.({ [name]: trimmed });
    };

    const showButtons = allowEdit || !sign;

    return (
        <Grid item xs={12} className="customeSign" style={style}>
            <span style={{ display: 'flex', alignItems: 'center' }}>
                <FormLabel
                    shrink
                    component="legend"
                    className={isRequired ? 'mandatoryfields' : ''}
                    required={!!isRequired}
                    style={{ marginBottom: !showButtons && state.isSignExist ? '8px' : '0px', fontSize: 12 }}
                >
                    {content}
                    &nbsp;
                    {showButtons && (
                        <>
                            <CustomButton icon={BorderColorIcon} onClick={useDefaultSig} toolTipTitle="Use default signature" />
                            <CustomButton icon={DeleteIcon} onClick={clearLeaderSign} IconColor="gray" toolTipTitle="Remove signature" />
                        </>
                    )}
                </FormLabel>
                {/* <FormLabel shrink> */}
                {/* </FormLabel> */}
            </span>

            <SignaturePad
                ref={signCanvas}
                onEnd={handleSignatureEnd}
                canvasProps={{
                    className: `${state.isSignExist ? 'existSignature' : 'signatureClass'}`
                }}
            />
        </Grid>
    );
};
